'use client';

import { useState, useEffect, useCallback } from 'react';
import { SectionShell } from '@/components/layout/SectionShell';

// ---------------------------------------------------------------------------
// Checklist data per pathway
// ---------------------------------------------------------------------------

interface ChecklistItem {
  id: string;
  /** Indonesian label */
  labelId: string;
  /** English label */
  labelEn: string;
}

const SNBP_CHECKLIST: ChecklistItem[] = [
  { id: 'print-letter', labelId: 'Cetak bukti kelulusan dari portal SNPMB', labelEn: 'Print acceptance letter from SNPMB portal' },
  { id: 'ukt-data', labelId: 'Isi data UKT (penghasilan orang tua)', labelEn: 'Submit UKT data (parents\' income)' },
  { id: 'documents', labelId: 'Siapkan dokumen (ijazah/SKL, rapor, akta, KK, foto)', labelEn: 'Prepare documents (diploma, transcripts, birth cert, family card, photos)' },
  { id: 'enrollment', labelId: 'Daftar ulang di portal universitas', labelEn: 'Complete enrollment on university portal' },
  { id: 'ukt-payment', labelId: 'Bayar UKT sesuai golongan', labelEn: 'Pay tuition (UKT) per assigned tier' },
  { id: 'pkkmb', labelId: 'Daftar PKKMB (orientasi mahasiswa baru)', labelEn: 'Register for PKKMB (new student orientation)' },
];

const SNBT_CHECKLIST: ChecklistItem[] = [
  { id: 'print-letter', labelId: 'Cetak bukti kelulusan dari portal SNPMB', labelEn: 'Print acceptance letter from SNPMB portal' },
  { id: 'ukt-data', labelId: 'Isi data UKT (penghasilan orang tua)', labelEn: 'Submit UKT data (parents\' income)' },
  { id: 'documents', labelId: 'Siapkan dokumen (ijazah/SKL, rapor, akta, KK, foto)', labelEn: 'Prepare documents (diploma, transcripts, birth cert, family card, photos)' },
  { id: 'utbk-score', labelId: 'Cetak sertifikat nilai UTBK', labelEn: 'Print UTBK score certificate' },
  { id: 'enrollment', labelId: 'Daftar ulang di portal universitas', labelEn: 'Complete enrollment on university portal' },
  { id: 'ukt-payment', labelId: 'Bayar UKT sesuai golongan', labelEn: 'Pay tuition (UKT) per assigned tier' },
  { id: 'pkkmb', labelId: 'Daftar PKKMB (orientasi mahasiswa baru)', labelEn: 'Register for PKKMB (new student orientation)' },
];

const MANDIRI_CHECKLIST: ChecklistItem[] = [
  { id: 'print-letter', labelId: 'Cetak bukti kelulusan dari portal universitas', labelEn: 'Print acceptance letter from university portal' },
  { id: 'ukt-data', labelId: 'Isi data UKT (penghasilan orang tua)', labelEn: 'Submit UKT data (parents\' income)' },
  { id: 'documents', labelId: 'Siapkan dokumen (ijazah/SKL, rapor, akta, KK, foto)', labelEn: 'Prepare documents (diploma, transcripts, birth cert, family card, photos)' },
  { id: 'enrollment', labelId: 'Daftar ulang di portal universitas', labelEn: 'Complete enrollment on university portal' },
  { id: 'ukt-payment', labelId: 'Bayar UKT/biaya kuliah', labelEn: 'Pay tuition (UKT/fees)' },
  { id: 'pkkmb', labelId: 'Daftar PKKMB (orientasi mahasiswa baru)', labelEn: 'Register for PKKMB (new student orientation)' },
];

function getChecklistForPathway(pathway: string): ChecklistItem[] {
  if (pathway.includes('snbp')) return SNBP_CHECKLIST;
  if (pathway.includes('snbt')) return SNBT_CHECKLIST;
  return MANDIRI_CHECKLIST;
}

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

function getStorageKey(universityId: string, pathway: string): string {
  return `almanak-checklist-${universityId}-${pathway}-2026`;
}

function loadChecked(key: string): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

function saveChecked(key: string, checked: Set<string>): void {
  try {
    localStorage.setItem(key, JSON.stringify([...checked]));
  } catch {
    // quota exceeded or private browsing
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface EnrollmentChecklistProps {
  /** University ID (e.g. 'ui', 'ugm') */
  universityId: string;
  /** University display name */
  universityName: string;
  /** Pathway filter string (e.g. 'new_student_snbp') */
  pathway: string;
  /** Language code */
  lang: string;
}

export function EnrollmentChecklist({ universityId, universityName, pathway, lang }: EnrollmentChecklistProps) {
  const isId = lang === 'id';
  const storageKey = getStorageKey(universityId, pathway);
  const items = getChecklistForPathway(pathway);

  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setChecked(loadChecked(storageKey));
    setMounted(true);
  }, [storageKey]);

  const toggle = useCallback((itemId: string) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      saveChecked(storageKey, next);
      return next;
    });
  }, [storageKey]);

  const completed = checked.size;
  const total = items.length;
  const progressPct = total > 0 ? Math.round((completed / total) * 100) : 0;

  const handleShare = () => {
    const abbrev = universityId.toUpperCase();
    const text = isId
      ? `Aku sudah ${completed}/${total} langkah daftar ulang di ${abbrev}! 🎓\n\nCek jadwalmu juga:`
      : `I've completed ${completed}/${total} enrollment steps at ${abbrev}! 🎓\n\nCheck your schedule too:`;
    const url = typeof window !== 'undefined' ? window.location.href : '';
    window.open(`https://wa.me/?text=${encodeURIComponent(text + '\n' + url)}`, '_blank');
    window._paq?.push(['trackEvent', 'Share', 'WhatsApp-Checklist', `${universityId}-${pathway}`]);
  };

  if (!mounted) {
    // SSR/hydration: render unchecked state
    return null;
  }

  return (
    <SectionShell className="pb-4">
      <div className="rounded-xl border border-border-subtle bg-surface-card p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-text-primary">
            {isId ? 'Checklist Daftar Ulang' : 'Enrollment Checklist'}
          </h3>
          <span className="text-xs text-text-secondary">
            {completed}/{total} {isId ? 'selesai' : 'done'}
          </span>
        </div>

        {/* Progress bar */}
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-surface-hover">
          <div
            className="h-full rounded-full bg-semantic-success transition-all duration-300"
            style={{ width: `${progressPct}%` }}
            role="progressbar"
            aria-valuenow={completed}
            aria-valuemin={0}
            aria-valuemax={total}
            aria-label={`${completed} of ${total} completed`}
          />
        </div>

        {/* Checklist items */}
        <ul className="mt-3 space-y-2">
          {items.map(item => {
            const isChecked = checked.has(item.id);
            return (
              <li key={item.id}>
                <label className="flex cursor-pointer items-start gap-2.5 text-sm">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggle(item.id)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-border-subtle text-semantic-success focus:ring-semantic-success"
                  />
                  <span className={isChecked ? 'text-text-secondary line-through' : 'text-text-primary'}>
                    {isId ? item.labelId : item.labelEn}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>

        {/* Share progress */}
        {completed > 0 && (
          <button
            onClick={handleShare}
            className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-semantic-success/10 px-3 py-1.5 text-xs font-medium text-semantic-success transition-colors hover:bg-semantic-success/20"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            {isId
              ? `Bagikan: "${completed}/${total} selesai di ${universityName}!"`
              : `Share: "${completed}/${total} done at ${universityName}!"`}
          </button>
        )}

        {completed === total && (
          <p className="mt-3 text-center text-sm font-medium text-semantic-success">
            🎉 {isId ? 'Semua langkah selesai! Selamat menjadi mahasiswa baru!' : 'All steps complete! Welcome, new student!'}
          </p>
        )}
      </div>
    </SectionShell>
  );
}
